<template>
  <header class="app-header" :class="{ 'is-scrolled': isScrolled }">
    <div class="container header-inner">
      <router-link to="/" class="logo">
        <span class="logo-mark" aria-hidden="true">
          <img v-if="showLogoImage" :src="site.logoUrl" alt="" class="logo-img">
          <span v-else class="logo-icon">{{ site.logoEmoji }}</span>
        </span>
        <span class="logo-text">
          <strong class="logo-title">{{ site.name }}</strong>
          <em v-if="site.logoSubtitle" class="logo-sub">{{ site.logoSubtitle }}</em>
        </span>
      </router-link>

      <nav class="nav-menu">
        <router-link
          v-for="(item, index) in navItems"
          :key="index"
          :to="getNavTo(item)"
          class="nav-item"
          :class="{ 'is-active': isNavActive(item) }"
        >
          {{ item.label }}
        </router-link>
      </nav>

      <div class="header-search">
        <el-input
          v-model="keyword"
          placeholder="搜索文章..."
          prefix-icon="el-icon-search"
          size="small"
          clearable
          @keyup.enter.native="handleSearch"
        />
      </div>

      <user-area />
    </div>
  </header>
</template>

<script>
import { siteConfig } from '@/config/site'
import { siteSettings } from '@/store/siteSettings'
import UserArea from '@/components/UserArea.vue'

export default {
  name: 'AppHeader',
  components: { UserArea },
  data() {
    return {
      isScrolled: false,
      keyword: ''
    }
  },
  computed: {
    site() {
      return siteSettings.config
    },
    navItems() {
      return siteSettings.navItems
    },
    showLogoImage() {
      return this.site.logoType === 'image' && this.site.logoUrl
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll)
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 10
    },
    getNavTo(item) {
      if (item.query) {
        return { path: item.path, query: item.query }
      }
      return item.path
    },
    isNavActive(item) {
      const { path, query } = this.$route

      if (item.path === '/') {
        return path === '/'
      }

      if (item.path === '/tools') {
        return path === '/tools' || path.startsWith('/tools/')
      }

      if (item.path === '/articles') {
        if (path !== '/articles') return false
        const currentType = query.type || ''
        const itemType = (item.query && item.query.type) || ''
        return currentType === itemType
      }

      return false
    },
    handleSearch() {
      if (this.keyword.trim()) {
        this.$router.push({ path: '/articles', query: { q: this.keyword.trim() } })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';

.app-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: $header-height;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(8px);
  border-bottom: 1px solid transparent;
  z-index: 1000;
  transition: all 0.3s ease;

  &.is-scrolled {
    border-bottom-color: $border-color;
    box-shadow: $shadow-sm;
  }
}

.header-inner {
  height: 100%;
  display: flex;
  align-items: center;
  gap: 32px;
}

.logo {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  text-decoration: none;

  &:hover {
    .logo-mark {
      transform: translateY(-1px);
      box-shadow: $shadow-md;
    }

    .logo-title {
      color: $primary-light;
    }
  }
}

.logo-mark {
  width: 38px;
  height: 38px;
  border-radius: 11px;
  background: linear-gradient(135deg, $primary-color 0%, $primary-light 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: $shadow-sm;
  transition: transform 0.25s, box-shadow 0.25s;
  overflow: hidden;
}

.logo-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.logo-icon {
  font-size: 20px;
  line-height: 1;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.12));
}

.logo-text {
  display: flex;
  flex-direction: column;
  gap: 1px;
  line-height: 1.2;
}

.logo-title {
  font-size: 18px;
  font-weight: 800;
  color: $primary-color;
  letter-spacing: 0.04em;
  transition: color 0.2s;
}

.logo-sub {
  font-style: normal;
  font-size: 10px;
  font-weight: 500;
  color: $text-muted;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.nav-menu {
  display: flex;
  gap: 4px;
  flex: 1;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.nav-item {
  padding: 8px 14px;
  border-radius: $radius-sm;
  color: $text-secondary;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.2s;

  &:hover {
    color: $primary-color;
    background: $primary-bg;
  }

  &.is-active {
    color: $primary-color;
    background: $primary-bg;
  }
}

.header-search {
  width: 180px;
  flex-shrink: 0;

  ::v-deep .el-input__inner {
    border-radius: 20px;
    border-color: $border-color;
    background: $bg-gray;

    &:focus {
      border-color: $primary-color;
    }
  }
}

@media (max-width: 992px) {
  .header-inner {
    gap: 12px;
  }

  .nav-item {
    padding: 6px 8px;
    font-size: 13px;
  }

  .header-search {
    width: 140px;
  }
}

@media (max-width: 768px) {
  .header-search {
    display: none;
  }

  .logo-text {
    display: none;
  }

  .logo-mark {
    width: 34px;
    height: 34px;
    border-radius: 10px;
  }
}
</style>
